import { useState, useRef, useEffect, useCallback } from "react";
import { MessageCircle, X, Send, Sparkles, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";

interface Message {
  role: "user" | "assistant";
  content: string;
}

function renderInline(text: string) {
  return text
    .split(/(\*\*[^*]+\*\*|`[^`]+`|\*[^*]+\*)/g)
    .map((part, k) => {
      if (part.startsWith("**") && part.endsWith("**"))
        return <strong key={k} className="font-semibold text-foreground">{part.slice(2, -2)}</strong>;
      if (part.startsWith("`") && part.endsWith("`"))
        return <code key={k} className="bg-muted px-1 py-0.5 rounded text-xs font-mono">{part.slice(1, -1)}</code>;
      if (part.startsWith("*") && part.endsWith("*") && !part.startsWith("**"))
        return <em key={k}>{part.slice(1, -1)}</em>;
      return <span key={k}>{part}</span>;
    });
}

function renderMarkdown(text: string) {
  const lines = text.split("\n");
  const elements: React.ReactNode[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    if (line.startsWith("### ")) {
      elements.push(<div key={i} className="font-semibold text-foreground text-sm mt-2 mb-1">{renderInline(line.slice(4))}</div>);
      i++; continue;
    }
    if (line.startsWith("## ")) {
      elements.push(<div key={i} className="font-semibold text-foreground text-sm mt-2 mb-1">{renderInline(line.slice(3))}</div>);
      i++; continue;
    }

    if (/^[*\-]\s+/.test(line)) {
      const listItems: React.ReactNode[] = [];
      while (i < lines.length && /^[*\-]\s+/.test(lines[i])) {
        listItems.push(<li key={i}>{renderInline(lines[i].replace(/^[*\-]\s+/, ""))}</li>);
        i++;
      }
      elements.push(<ul key={`ul-${i}`} className="list-disc pl-4 space-y-0.5">{listItems}</ul>);
      continue;
    }

    if (/^\d+\.\s+/.test(line)) {
      const listItems: React.ReactNode[] = [];
      while (i < lines.length && /^\d+\.\s+/.test(lines[i])) {
        listItems.push(<li key={i}>{renderInline(lines[i].replace(/^\d+\.\s+/, ""))}</li>);
        i++;
      }
      elements.push(<ol key={`ol-${i}`} className="list-decimal pl-4 space-y-0.5">{listItems}</ol>);
      continue;
    }

    if (!line.trim()) {
      elements.push(<div key={i} className="h-1.5" />);
      i++; continue;
    }

    elements.push(<div key={i}>{renderInline(line)}</div>);
    i++;
  }

  return elements;
}

const DEFAULT_WELCOME = "Bonjour ! 👋 Je suis l'assistant Magnetiq. Posez-moi une question sur la plateforme et je vous répondrai à partir de notre documentation.";

/* ───────── Reusable chat panel (no wrapper) ───────── */
export function ChatbotPanel({ welcomeMessage, subtitle = "Basé sur le centre d'aide" }: { welcomeMessage?: string; subtitle?: string }) {
  const resolvedWelcome = welcomeMessage || DEFAULT_WELCOME;
  const [messages, setMessages] = useState<Message[]>([
    { role: "assistant", content: resolvedWelcome },
  ]);

  useEffect(() => {
    setMessages([{ role: "assistant", content: resolvedWelcome }]);
  }, [resolvedWelcome]);

  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const scrollToBottom = useCallback(() => {
    setTimeout(() => {
      if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }, 50);
  }, []);

  useEffect(() => { scrollToBottom(); setTimeout(() => inputRef.current?.focus(), 100); }, [scrollToBottom]);
  useEffect(() => { scrollToBottom(); }, [messages, scrollToBottom]);

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || loading) return;
    const userMsg: Message = { role: "user", content: text };
    const newMessages = [...messages, userMsg];
    setMessages(newMessages);
    setInput("");
    setLoading(true);
    try {
      const apiMessages = newMessages.map((m) => ({ role: m.role, content: m.content }));
      const { data, error } = await supabase.functions.invoke("help-chatbot", { body: { messages: apiMessages } });
      if (error) throw error;
      const reply = data?.reply || "Désolé, je n'ai pas pu répondre.";
      setMessages((prev) => [...prev, { role: "assistant", content: reply }]);
    } catch (err) {
      console.error("Chatbot error:", err);
      setMessages((prev) => [...prev, { role: "assistant", content: "Désolé, une erreur est survenue. Réessayez dans un instant." }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Messages */}
      <ScrollArea className="flex-1 min-h-0">
        <div ref={scrollRef} className="p-4 space-y-3 h-full overflow-y-auto">
          {messages.map((msg, i) => (
            <div key={i} className={cn("flex gap-2", msg.role === "user" ? "justify-end" : "justify-start")}>
              {msg.role === "assistant" && (
                <div className="w-6 h-6 rounded-md bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
                  <Sparkles className="w-3 h-3 text-primary" />
                </div>
              )}
              <div className={cn("max-w-[85%] rounded-xl px-3 py-2 text-sm leading-relaxed", msg.role === "user" ? "bg-primary/10 text-foreground" : "bg-muted/50 text-foreground")}>
                {renderMarkdown(msg.content)}
              </div>
            </div>
          ))}
          {loading && (
            <div className="flex gap-2 justify-start">
              <div className="w-6 h-6 rounded-md bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
                <Sparkles className="w-3 h-3 text-primary" />
              </div>
              <div className="bg-muted/50 rounded-xl px-3 py-2">
                <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
              </div>
            </div>
          )}
        </div>
      </ScrollArea>

      {/* Input */}
      <div className="border-t border-border p-3 flex gap-2">
        <input
          ref={inputRef}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Posez votre question…"
          disabled={loading}
          className="flex-1 bg-muted/30 border border-border rounded-lg px-3 py-2 text-base sm:text-sm outline-none focus:ring-2 focus:ring-ring placeholder:text-muted-foreground disabled:opacity-50"
        />
        <Button size="icon" onClick={sendMessage} disabled={loading || !input.trim()} className="h-9 w-9 shrink-0">
          <Send className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}

/* ───────── Floating widget (public / marketing pages) ───────── */
interface ChatbotWidgetProps {
  welcomeMessage?: string;
  subtitle?: string;
}

export default function ChatbotWidget({ welcomeMessage, subtitle = "Basé sur le centre d'aide" }: ChatbotWidgetProps = {}) {
  const [open, setOpen] = useState(false);

  return (
    <>
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-6 right-6 z-50 w-12 h-12 rounded-full bg-primary text-primary-foreground shadow-lg hover:scale-105 transition-transform flex items-center justify-center animate-pulse"
          aria-label="Ouvrir l'assistant"
        >
          <MessageCircle className="w-5 h-5" />
        </button>
      )}

      {open && (
        <div className="fixed inset-0 z-50 flex flex-col bg-background overflow-hidden sm:inset-auto sm:bottom-6 sm:right-6 sm:w-[380px] sm:h-[500px] sm:max-h-[calc(100vh-4rem)] sm:rounded-2xl sm:border sm:border-border sm:shadow-2xl">
          <div className="flex items-center gap-3 px-4 py-3 border-b border-border bg-muted/30">
            <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-sm">Assistant Magnetiq</div>
              <div className="text-xs text-muted-foreground">{subtitle}</div>
            </div>
            <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={() => setOpen(false)}>
              <X className="w-4 h-4" />
            </Button>
          </div>
          <ChatbotPanel welcomeMessage={welcomeMessage} subtitle={subtitle} />
        </div>
      )}
    </>
  );
}
